import {
  Box,
  Body,
  Space,
  Button,
  ProfileIcon,
  HomeIcon,
  BadgeIcon,
  AtIcon,
  PaymentIcon,
  PhoneCameraIcon,
  FeatureList,
  Stack,
  Banner,
  UniCreditIconProps,
  LocalizedString,
} from "@dbn/omnichannel-design-system-g4p";
import { palette } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { Option } from "fp-ts/Option";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { constVoid, pipe } from "fp-ts/function";
import { option } from "fp-ts";
import { NonEmptyString } from "io-ts-types/NonEmptyString";
import { PromoForm } from "../PromoForm";
import { useFormatMessage } from "../../intl";
import { foldTenant, Tenant } from "../../globalDomain";
import { useAppContext } from "../../useAppContext";
import { useIsRemoteChannel } from "../../useChannel";

type ListItemProps = {
  icon: (props: UniCreditIconProps) => JSX.Element;
  label: LocalizedString;
  iconAriaHidden?: boolean;
  iconAriaLabel?: LocalizedString;
};

export function WhatWillNeed(props: {
  initialPromoCode: Option<NonEmptyString>;
  onAccept: () => unknown;
  tenant: Tenant;
}) {
  const formatMessage = useFormatMessage();
  const {
    config: { promotionsEnabled },
  } = useAppContext();

  const isRemote = useIsRemoteChannel();

  const requirementsCZ: NonEmptyArray<ListItemProps> = [
    {
      icon: HomeIcon,
      label: formatMessage(
        "Identification.MainContent.whatWillNeed.CZ.Citizen"
      ),
      iconAriaHidden: true,
    },
    {
      icon: ProfileIcon,
      label: formatMessage("Identification.MainContent.whatWillNeed.CZ.Age"),
      iconAriaHidden: true,
    },
    {
      icon: BadgeIcon,
      label: formatMessage("Identification.MainContent.whatWillNeed.CZ.IDCard"),
      iconAriaHidden: true,
    },
    {
      icon: PaymentIcon,
      label: formatMessage(
        "Identification.MainContent.whatWillNeed.CZ.BankAccount"
      ),
      iconAriaHidden: true,
    },
    {
      icon: AtIcon,
      label: formatMessage(
        "Identification.MainContent.whatWillNeed.CZ.MobilePhone"
      ),
      iconAriaHidden: true,
    },
  ];

  const requirementsSK: NonEmptyArray<ListItemProps> = [
    {
      icon: HomeIcon,
      label: formatMessage(
        "Identification.MainContent.whatWillNeed.SK.Citizen"
      ),
      iconAriaHidden: true,
    },
    {
      icon: ProfileIcon,
      label: formatMessage("Identification.MainContent.whatWillNeed.SK.Age"),
      iconAriaHidden: true,
    },
    {
      icon: BadgeIcon,
      label: formatMessage("Identification.MainContent.whatWillNeed.SK.IDCard"),
      iconAriaHidden: true,
    },
    {
      icon: AtIcon,
      label: formatMessage(
        "Identification.MainContent.whatWillNeed.SK.MobilePhone"
      ),
      iconAriaHidden: true,
    },
    {
      icon: PhoneCameraIcon,
      label: formatMessage(
        "Identification.MainContent.whatWillNeed.SK.PhoneCamera"
      ),
      iconAriaHidden: true,
    },
  ];

  return (
    <Box column grow>
      <Stack column units={12}>
        <Body size="medium" weight="regular" color={palette.dark}>
          {formatMessage("Identification.MainContent.whatWillNeed.body")}
        </Body>
        {pipe(
          foldTenant(
            props.tenant,
            () => requirementsSK,
            () => requirementsCZ
          ),
          requirements => (
            <FeatureList size="large" children={requirements} />
          )
        )}
        {promotionsEnabled && (
          <PromoForm
            initialPromoCode={props.initialPromoCode}
            onApply={constVoid}
            onRemove={constVoid}
            onValidFormChange={constVoid}
          />
        )}
      </Stack>
      <Space units={6} />
      {isRemote && (
        <Banner
          actions={option.none}
          content={formatMessage(
            "Identification.MainContent.whatWillNeed.newClientsOnlyWarning"
          )}
          title={option.none}
          type="informative"
          onDismiss={option.none}
        />
      )}
      <Space units={6} />
      <Box hAlignContent="right">
        <Button
          variant="primary"
          size="default"
          action={props.onAccept}
          label={formatMessage("Identification.MainContent.whatWillNeed.CTA")}
        />
      </Box>
    </Box>
  );
}
